import {
  ActionableNotification,
  InlineNotification,
  Modal,
} from '@carbon/react';
import { ProvideRootResource } from 'components/hydrate-atoms/hydrate-atoms';
import { useAtom, useAtomValue, useSetAtom, useStore } from 'jotai';
import { api } from 'lib/request/api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import routes from '~/routes';
import { formatNumber } from 'utils/number';
import { tractEnumLoadableAtom } from '../atoms';
import { AssetTearsheet, EditorState } from './asset-tearsheet';
import {
  MAX_BATCH_ROWS,
  batchDefaultAtom,
  bulkStepAtom,
  canProceedAtom,
  curationRowsAtom,
  dryRunErrorAtom,
  dryRunRetryAtom,
  filteredRowsAtom,
  isRowValid,
  markRowErrorsAtom,
  uploadedFilesAtom,
  validityCountsAtom,
} from './atoms';
import { BatchDefaultCard } from './batch-default-card';
import { creatingSummary, reviewSummary } from './batch';
import { BulkMap } from './bulk-map';
import styles from './bulk-upload.module.scss';
import { CurationTable } from './curation-table';
import {
  RequestFailure,
  defaultParams,
  requestFailure,
  useDryRunValidation,
} from './hooks';
import { RequestFailureBanner } from './request-failure-banner';
import {
  BulkUploadShell,
  CREATING_ACTIONS,
  FooterAction,
  useCreatePermission,
} from './shell';
import type { BulkAssetBatch, CurationRow } from './types';
import { UploadPanel } from './upload-panel';
import { curationMapRow } from './map';

export { TEMPLATE_HREF } from './shell';

/** Validating up to MAX_BATCH_ROWS rows runs inside the submit request. */
const SUBMIT_TIMEOUT_MS = 120_000;

/**
 * The inline map over every curated row, valid or not.
 * @return {JSX.Element | null} The map, or null while hidden
 */
const CurationMap = () => {
  const rows = useAtomValue(curationRowsAtom);
  const mapRows = useMemo(() => rows.map(curationMapRow), [rows]);
  return <BulkMap rows={mapRows} />;
};

interface CurationStepProps {
  failure: RequestFailure | null;
  /** Absent while the rows cannot be created, as the footer button is disabled. */
  onTryAgain?: () => void;
}

/**
 * The curation step: the batch default, what will be created and skipped, the
 * working table, and the upload panel and asset tearsheet it opens. A failed
 * submit lands back here with its banner.
 * @param {CurationStepProps} props - A failed submit, and how to resend it
 * @return {JSX.Element} The step body
 */
const CurationStep = ({ failure, onTryAgain }: CurationStepProps) => {
  const rowCount = useAtomValue(curationRowsAtom).length;
  const { valid, invalid } = useAtomValue(validityCountsAtom);
  const filteredRows = useAtomValue(filteredRowsAtom);
  const enums = useAtomValue(tractEnumLoadableAtom);
  const dryRunError = useAtomValue(dryRunErrorAtom);
  const setRetry = useSetAtom(dryRunRetryAtom);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editor, setEditor] = useState<EditorState | null>(null);

  useDryRunValidation();

  const openAdd = useCallback(() => {
    setUploadOpen(false);
    setEditor({ mode: 'add' });
  }, []);
  const openUpload = useCallback(() => {
    setEditor(null);
    setUploadOpen(true);
  }, []);
  const openEdit = useCallback(
    (row: CurationRow) => {
      setUploadOpen(false);
      setEditor({
        mode: 'edit',
        clientId: row.clientId,
        walk: filteredRows.map((candidate) => candidate.clientId),
      });
    },
    [filteredRows]
  );

  return (
    <div className={styles.step}>
      {failure && (
        <RequestFailureBanner failure={failure} onTryAgain={onTryAgain} />
      )}
      {enums.state === 'hasError' && (
        <InlineNotification
          kind="error"
          title="Failed to load enum data"
          subtitle="Unable to load the asset field options. Please refresh the page."
        />
      )}
      {dryRunError && (
        <ActionableNotification
          inline
          kind="error"
          lowContrast
          title={dryRunError}
          actionButtonLabel="Try again"
          onActionButtonClick={() => setRetry((n) => n + 1)}
          hideCloseButton
        />
      )}
      {rowCount > MAX_BATCH_ROWS && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          title={`A batch holds at most ${formatNumber({
            value: MAX_BATCH_ROWS,
            minPrecision: 0,
          })} assets. Remove ${formatNumber({
            value: rowCount - MAX_BATCH_ROWS,
            minPrecision: 0,
          })} to continue.`}
        />
      )}
      <BatchDefaultCard />
      {rowCount > 0 && (
        <p className={styles.summary}>{reviewSummary(valid, invalid)}</p>
      )}
      <CurationMap />
      <CurationTable
        mode="edit"
        onAdd={openAdd}
        onUpload={openUpload}
        onEdit={openEdit}
      />
      <UploadPanel open={uploadOpen} onClose={() => setUploadOpen(false)} />
      {editor && (
        <AssetTearsheet
          key={editor.mode === 'edit' ? editor.clientId : 'add'}
          editor={editor}
          onClose={() => setEditor(null)}
          onNavigate={(clientId) =>
            setEditor((current) => {
              return current?.mode === 'edit'
                ? { ...current, clientId }
                : current;
            })
          }
        />
      )}
    </div>
  );
};

/**
 * Creating, while the submit is in flight: the curated rows read-only.
 * @return {JSX.Element} The step body
 */
const CreatingStep = () => {
  const { valid, invalid } = useAtomValue(validityCountsAtom);
  return (
    <div className={styles.step}>
      <p className={styles.summary}>{creatingSummary(valid, invalid)}</p>
      <CurationMap />
      <CurationTable mode="creating" />
    </div>
  );
};

/**
 * Bulk Upload Assets: a full page reached from the CRM page's Create CRM Asset
 * menu. Everything on it is browser state until Bulk Create Assets submits the
 * batch; then the page moves to the batch's own URL, which shows the results.
 * @return {JSX.Element} The page
 */
export const BulkUploadContent = () => {
  const history = useHistory();
  const store = useStore();
  const permission = useCreatePermission();
  const [step, setStep] = useAtom(bulkStepAtom);
  const hasRows = useAtomValue(curationRowsAtom).length > 0;
  const canProceed = useAtomValue(canProceedAtom);
  const { valid, invalid } = useAtomValue(validityCountsAtom);
  const markRowErrors = useSetAtom(markRowErrorsAtom);
  const [failure, setFailure] = useState<RequestFailure | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [batchId, setBatchId] = useState<number | null>(null);

  // After the guard has re-rendered without changes, so it does not ask.
  useEffect(() => {
    if (batchId === null) return;
    history.replace(generatePath(routes.crmBulkUploadPath, { id: batchId }));
  }, [batchId, history]);

  const submit = async () => {
    setConfirming(false);
    setFailure(null);
    setStep('creating');
    const rows = store.get(curationRowsAtom);
    try {
      const batch: BulkAssetBatch = await (
        await api.post('crm/bulk_asset_batches', {
          json: {
            ...defaultParams(store.get(batchDefaultAtom)),
            sourceFileNames: store
              .get(uploadedFilesAtom)
              .filter((file) => file.status === 'loaded')
              .map((file) => file.name),
            rows: rows
              .filter(isRowValid)
              .map(({ position, values }) => ({ position, values })),
            // Stored as skipped, so the results list every row.
            skippedRows: rows
              .filter((row) => !isRowValid(row))
              .map(({ position, values }) => ({ position, values })),
          },
          timeout: SUBMIT_TIMEOUT_MS,
        })
      ).json();
      setBatchId(batch.id);
    } catch (error) {
      const next = await requestFailure(error);
      if (next.kind === 'rows') markRowErrors(next.rows);
      setFailure(next);
      setStep('entry');
    }
  };

  const footerActions = (): FooterAction[] => {
    if (step === 'entry') {
      return [
        {
          key: 'cancel',
          kind: 'ghost',
          label: 'Cancel',
          onClick: () => history.push(routes.crmTractsAndSubtractsPath),
        },
        {
          key: 'create',
          kind: 'primary',
          label: 'Bulk Create Assets',
          disabled: !permission.allowed || !canProceed,
          onClick: () => setConfirming(true),
        },
      ];
    }
    return CREATING_ACTIONS;
  };

  return (
    <BulkUploadShell
      hasChanges={hasRows && batchId === null}
      actions={footerActions()}
    >
      {step === 'entry' ? (
        <CurationStep
          failure={failure}
          onTryAgain={permission.allowed && canProceed ? submit : undefined}
        />
      ) : (
        <CreatingStep />
      )}
      {confirming && (
        <Modal
          open
          modalHeading="Bulk create assets?"
          primaryButtonText="Bulk Create Assets"
          secondaryButtonText="Cancel"
          onRequestClose={() => setConfirming(false)}
          onRequestSubmit={submit}
        >
          <p>{reviewSummary(valid, invalid)}</p>
        </Modal>
      )}
    </BulkUploadShell>
  );
};

/**
 * Mounts the page in its own jotai store so each visit starts empty.
 * @return {JSX.Element} The page
 */
const BulkUploadPage = () => (
  <ProvideRootResource>
    <BulkUploadContent />
  </ProvideRootResource>
);

export default BulkUploadPage;
